import { supabase } from '../config/supabase';
import { User, QuizResult, LessonProgress, FlashcardProgress, ActivityLog, DailyStreak, Message } from '../types';

// Re-export supabase client for direct usage
export { supabase };

// ============================================
// USER FUNCTIONS
// ============================================

export async function getOrCreateUser(privyId: string, name: string, email: string): Promise<User | null> {
  // Try to get existing user
  const { data: existingUser } = await supabase
    .from('users')
    .select('*')
    .eq('privy_id', privyId)
    .single();

  if (existingUser) {
    return existingUser as User;
  }

  // Create new user
  const { data: newUser, error } = await supabase
    .from('users')
    .insert({
      privy_id: privyId,
      name,
      email,
      role: 'student', // Default role
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating user:', error);
    return null;
  }

  return newUser as User;
}

export async function getUserChildren(parentId: string): Promise<User[]> {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('parent_id', parentId);

  if (error) {
    console.error('Error fetching children:', error);
    return [];
  }

  return data as User[];
}

// ============================================
// QUIZ RESULTS FUNCTIONS
// ============================================

export async function saveQuizResult(result: Omit<QuizResult, 'id' | 'created_at'>): Promise<QuizResult | null> {
  const { data, error } = await supabase
    .from('quiz_results')
    .insert(result)
    .select()
    .single();

  if (error) {
    console.error('Error saving quiz result:', error);
    return null;
  }

  return data as QuizResult;
}

export async function getQuizResults(userId: string, courseId?: string, limit = 10): Promise<QuizResult[]> {
  let query = supabase
    .from('quiz_results')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (courseId) {
    query = query.eq('course_id', courseId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching quiz results:', error);
    return [];
  }

  return data as QuizResult[];
}

// ============================================
// LESSON PROGRESS FUNCTIONS
// ============================================

export async function markLessonComplete(userId: string, courseId: string, lessonId: string): Promise<void> {
  const { error } = await supabase
    .from('lesson_progress')
    .upsert({
      user_id: userId,
      course_id: courseId,
      lesson_id: lessonId,
      completed: true,
      listened_at: new Date().toISOString(),
    });

  if (error) {
    console.error('Error marking lesson complete:', error);
  }
}

export async function getLessonProgress(userId: string, courseId: string): Promise<LessonProgress[]> {
  const { data, error } = await supabase
    .from('lesson_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('course_id', courseId);

  if (error) {
    console.error('Error fetching lesson progress:', error);
    return [];
  }

  return data as LessonProgress[];
}

// ============================================
// FLASHCARD PROGRESS FUNCTIONS
// ============================================

export async function saveFlashcardResult(
  userId: string,
  courseId: string,
  flashcardId: string,
  knew: boolean
): Promise<void> {
  const { error } = await supabase
    .from('flashcard_progress')
    .upsert({
      user_id: userId,
      course_id: courseId,
      flashcard_id: flashcardId,
      knew,
      last_practiced: new Date().toISOString(),
    });

  if (error) {
    console.error('Error saving flashcard result:', error);
  }
}

export async function getFlashcardProgress(userId: string, courseId: string): Promise<FlashcardProgress[]> {
  const { data, error } = await supabase
    .from('flashcard_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('course_id', courseId);

  if (error) {
    console.error('Error fetching flashcard progress:', error);
    return [];
  }

  return data as FlashcardProgress[];
}

// ============================================
// STREAK FUNCTIONS
// ============================================

export async function updateDailyStreak(userId: string, activity: string, minutes: number): Promise<void> {
  const today = new Date().toISOString().split('T')[0];

  // Get existing streak for today
  const { data: existing } = await supabase
    .from('daily_streaks')
    .select('*')
    .eq('user_id', userId)
    .eq('date', today)
    .single();

  if (existing) {
    // Update existing
    const activities = [...(existing.activities || []), activity];
    const totalMinutes = (existing.total_minutes || 0) + minutes;

    await supabase
      .from('daily_streaks')
      .update({ activities, total_minutes: totalMinutes })
      .eq('id', existing.id);
  } else {
    // Create new
    await supabase
      .from('daily_streaks')
      .insert({
        user_id: userId,
        date: today,
        activities: [activity],
        total_minutes: minutes,
      });
  }
}

export async function getStreak(userId: string): Promise<number> {
  const { data, error } = await supabase
    .from('daily_streaks')
    .select('date')
    .eq('user_id', userId)
    .order('date', { ascending: false })
    .limit(30);

  if (error || !data || data.length === 0) {
    return 0;
  }

  // Count consecutive days
  let streak = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < data.length; i++) {
    const streakDate = new Date(data[i].date);
    streakDate.setHours(0, 0, 0, 0);

    const expectedDate = new Date(today);
    expectedDate.setDate(expectedDate.getDate() - i);

    if (streakDate.getTime() === expectedDate.getTime()) {
      streak++;
    } else {
      break;
    }
  }

  return streak;
}

// ============================================
// ACTIVITY LOG FUNCTIONS
// ============================================

export async function logActivity(
  userId: string,
  action: string,
  details: Record<string, any> = {}
): Promise<void> {
  try {
    // Activity Log schreiben
    const { error } = await supabase
      .from('activity_log')
      .insert({
        user_id: userId,
        action,
        details,
      });

    if (error) {
      console.error('Error logging activity:', error);
    }

    // last_active updaten
    await supabase
      .from('users')
      .update({ last_active: new Date().toISOString() })
      .eq('id', userId);

    // Daily Streak updaten
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

    // Prüfe ob heute schon ein Streak-Eintrag existiert
    const { data: existing } = await supabase
      .from('daily_streaks')
      .select('*')
      .eq('user_id', userId)
      .eq('date', today)
      .single();

    if (existing) {
      // Update: Aktivität hinzufügen, Minuten erhöhen
      const activities = existing.activities || [];
      activities.push({ action, time: new Date().toISOString(), ...details });
      await supabase.from('daily_streaks').update({
        activities,
        total_minutes: (existing.total_minutes || 0) + (details.duration_minutes || 1),
      }).eq('id', existing.id);
    } else {
      // Neu: Erster Eintrag für heute
      await supabase.from('daily_streaks').insert({
        user_id: userId,
        date: today,
        activities: [{ action, time: new Date().toISOString(), ...details }],
        total_minutes: details.duration_minutes || 1,
      });
    }
  } catch (error) {
    console.error('logActivity error:', error);
    // Fehler nicht an User weiterleiten, nur loggen
  }
}

// ============================================
// ADMIN FUNCTIONS
// ============================================

export async function getAllUsers(): Promise<User[]> {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching users:', error);
    return [];
  }

  return data as User[];
}

export async function getAllQuizResults(limit = 100): Promise<(QuizResult & { user_name?: string; user_email?: string })[]> {
  const { data, error } = await supabase
    .from('quiz_results')
    .select(`
      *,
      users (name, email)
    `)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching all quiz results:', error);
    return [];
  }

  // Flatten the joined data
  return data.map((item: any) => ({
    ...item,
    user_name: item.users?.name,
    user_email: item.users?.email,
    users: undefined,
  }));
}

export async function getActivityLog(
  limit = 100,
  userId?: string,
  action?: string
): Promise<ActivityLog[]> {
  let query = supabase
    .from('activity_log')
    .select(`
      *,
      users (name, email)
    `)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (userId) {
    query = query.eq('user_id', userId);
  }

  if (action) {
    query = query.eq('action', action);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching activity log:', error);
    return [];
  }

  // Flatten the joined data
  return data.map((item: any) => ({
    ...item,
    user_name: item.users?.name,
    user_email: item.users?.email,
    users: undefined,
  }));
}

export async function getAllStreaks(): Promise<(DailyStreak & { user_name?: string })[]> {
  const { data, error } = await supabase
    .from('daily_streaks')
    .select(`
      *,
      users (name)
    `)
    .order('date', { ascending: false })
    .limit(200);

  if (error) {
    console.error('Error fetching all streaks:', error);
    return [];
  }

  return data.map((item: any) => ({
    ...item,
    user_name: item.users?.name,
    users: undefined,
  }));
}

export async function getUserStats(): Promise<{
  totalUsers: number;
  totalQuizzes: number;
  avgScore: number;
  activeToday: number;
}> {
  // Get total users
  const { count: totalUsers } = await supabase
    .from('users')
    .select('*', { count: 'exact', head: true });

  // Get quiz stats
  const { data: quizStats } = await supabase
    .from('quiz_results')
    .select('percentage');

  const totalQuizzes = quizStats?.length || 0;
  const avgScore = totalQuizzes > 0
    ? Math.round(quizStats!.reduce((sum, q) => sum + q.percentage, 0) / totalQuizzes)
    : 0;

  // Get active today
  const today = new Date().toISOString().split('T')[0];
  const { count: activeToday } = await supabase
    .from('daily_streaks')
    .select('*', { count: 'exact', head: true })
    .eq('date', today);

  return {
    totalUsers: totalUsers || 0,
    totalQuizzes,
    avgScore,
    activeToday: activeToday || 0,
  };
}

// ============================================
// AI FEEDBACK FUNCTIONS
// ============================================

export interface AiFeedback {
  id: string;
  user_id: string;
  type: 'lernstand' | 'written_quiz';
  course_id: string;
  question: string | null;
  user_answer: string | null;
  ai_response: string;
  parsed_data: Record<string, string>;
  score_info: string | null;
  created_at: string;
}

export async function saveAiFeedback(
  userId: string,
  type: 'lernstand' | 'written_quiz' | 'transform_quiz' | 'fill_in_quiz' | 'math_exercise_help',
  aiResponse: string,
  parsedData: Record<string, string>,
  options?: {
    courseId?: string;
    question?: string;
    userAnswer?: string;
    scoreInfo?: string;
  }
): Promise<void> {
  try {
    const { error } = await supabase.from('ai_feedback').insert({
      user_id: userId,
      type,
      course_id: options?.courseId || 'eu-geographie',
      question: options?.question || null,
      user_answer: options?.userAnswer || null,
      ai_response: aiResponse,
      parsed_data: parsedData,
      score_info: options?.scoreInfo || null,
    });

    if (error) {
      console.error('saveAiFeedback error:', error);
    }
  } catch (error) {
    console.error('saveAiFeedback error:', error);
  }
}

export async function getAiFeedbackHistory(
  userId: string,
  limit: number = 50,
  courseId?: string
): Promise<AiFeedback[]> {
  let query = supabase
    .from('ai_feedback')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (courseId) {
    query = query.eq('course_id', courseId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('getAiFeedbackHistory error:', error);
    return [];
  }

  return (data || []) as AiFeedback[];
}

// ============================================
// COURSE COMPLETION STATUS
// ============================================

export interface CourseCompletionStatus {
  quizDone: boolean;
  quizPassed: boolean;
  quizPercentage: number | null;
  fillInDone: boolean;
  fillInPassed: boolean;
  fillInPercentage: number | null;
  transformDone: boolean;
  transformPassed: boolean;
  transformPercentage: number | null;
  vocabWeaknesses: number;
  flashcardWeaknesses: number;
  allComplete: boolean;
}

export async function getCourseCompletionStatus(
  userId: string,
  courseId: string
): Promise<CourseCompletionStatus> {
  const status: CourseCompletionStatus = {
    quizDone: false,
    quizPassed: false,
    quizPercentage: null,
    fillInDone: false,
    fillInPassed: false,
    fillInPercentage: null,
    transformDone: false,
    transformPassed: false,
    transformPercentage: null,
    vocabWeaknesses: 0,
    flashcardWeaknesses: 0,
    allComplete: false,
  };

  try {
    // 1. Check Quiz results (quiz_results table)
    const { data: quizResults } = await supabase
      .from('quiz_results')
      .select('percentage')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .order('created_at', { ascending: false })
      .limit(1);

    if (quizResults && quizResults.length > 0) {
      status.quizDone = true;
      status.quizPercentage = quizResults[0].percentage;
      status.quizPassed = quizResults[0].percentage >= 90;
    }

    // 2. Check FillIn results (activity_log table - check both old and new action names)
    const { data: fillInLogs } = await supabase
      .from('activity_log')
      .select('details')
      .eq('user_id', userId)
      .in('action', ['fill_in', 'fill_in_quiz'])
      .order('created_at', { ascending: false })
      .limit(1);

    if (fillInLogs && fillInLogs.length > 0) {
      status.fillInDone = true;
      const details = fillInLogs[0].details as {
        score?: number;
        total?: number;
        percentage?: number;
        correct?: number;
        questions_answered?: number;
      };
      // Handle both new format (score/total/percentage) and old format (correct/questions_answered)
      if (details.percentage !== undefined) {
        status.fillInPercentage = details.percentage;
        status.fillInPassed = details.percentage >= 90;
      } else if (details.score !== undefined && details.total) {
        status.fillInPercentage = Math.round((details.score / details.total) * 100);
        status.fillInPassed = status.fillInPercentage >= 90;
      } else if (details.correct !== undefined && details.questions_answered) {
        status.fillInPercentage = Math.round((details.correct / details.questions_answered) * 100);
        status.fillInPassed = status.fillInPercentage >= 90;
      }
    }

    // 3. Check Transform results (activity_log first, fallback to ai_feedback)
    const { data: transformLogs } = await supabase
      .from('activity_log')
      .select('details')
      .eq('user_id', userId)
      .eq('action', 'transform_quiz')
      .order('created_at', { ascending: false })
      .limit(1);

    if (transformLogs && transformLogs.length > 0) {
      status.transformDone = true;
      const details = transformLogs[0].details as {
        score?: number;
        total?: number;
        percentage?: number;
      };
      if (details.percentage !== undefined) {
        status.transformPercentage = details.percentage;
        status.transformPassed = details.percentage >= 90;
      } else if (details.score !== undefined && details.total) {
        status.transformPercentage = Math.round((details.score / details.total) * 100);
        status.transformPassed = status.transformPercentage >= 90;
      }
    } else {
      // Fallback: Check ai_feedback table for older entries
      const { data: transformFeedback } = await supabase
        .from('ai_feedback')
        .select('score_info, created_at')
        .eq('user_id', userId)
        .eq('type', 'transform_quiz')
        .order('created_at', { ascending: false })
        .limit(10);

      if (transformFeedback && transformFeedback.length > 0) {
        status.transformDone = true;
        // Get results from last session (within 30 minutes of most recent)
        const latestTime = new Date(transformFeedback[0].created_at).getTime();
        const sessionResults = transformFeedback.filter(f => {
          const diff = latestTime - new Date(f.created_at).getTime();
          return diff < 30 * 60 * 1000; // 30 minutes
        });

        // Parse scores (format: "X/Y Punkte" or "X von Y")
        let totalEarned = 0;
        let totalMax = 0;
        for (const result of sessionResults) {
          if (result.score_info) {
            const match = result.score_info.match(/(\d+)\s*[\/von]\s*(\d+)/i);
            if (match) {
              totalEarned += parseInt(match[1], 10);
              totalMax += parseInt(match[2], 10);
            }
          }
        }
        if (totalMax > 0) {
          status.transformPercentage = Math.round((totalEarned / totalMax) * 100);
          status.transformPassed = status.transformPercentage >= 90;
        }
      }
    }

    // 4. Check Flashcard & Vocab weaknesses
    const { data: flashcardProgress } = await supabase
      .from('flashcard_progress')
      .select('flashcard_id, knew')
      .eq('user_id', userId)
      .eq('course_id', courseId);

    if (flashcardProgress) {
      status.vocabWeaknesses = flashcardProgress.filter(
        p => !p.knew && p.flashcard_id.startsWith('vocab_')
      ).length;
      status.flashcardWeaknesses = flashcardProgress.filter(
        p => !p.knew && !p.flashcard_id.startsWith('vocab_')
      ).length;
    }

    // Check if all conditions are met
    status.allComplete =
      status.quizDone && status.quizPassed &&
      status.fillInDone && status.fillInPassed &&
      status.transformDone && status.transformPassed &&
      status.vocabWeaknesses === 0 &&
      status.flashcardWeaknesses === 0;

  } catch (error) {
    console.error('getCourseCompletionStatus error:', error);
  }

  return status;
}

export async function resetCourseProgress(
  userId: string,
  courseId: string
): Promise<boolean> {
  try {
    // Delete quiz_results for this course
    await supabase
      .from('quiz_results')
      .delete()
      .eq('user_id', userId)
      .eq('course_id', courseId);

    // Delete flashcard_progress for this course
    await supabase
      .from('flashcard_progress')
      .delete()
      .eq('user_id', userId)
      .eq('course_id', courseId);

    // Delete lesson_progress for this course
    await supabase
      .from('lesson_progress')
      .delete()
      .eq('user_id', userId)
      .eq('course_id', courseId);

    // Delete ai_feedback for this course
    await supabase
      .from('ai_feedback')
      .delete()
      .eq('user_id', userId)
      .eq('course_id', courseId);

    // Delete activity_log entries for this course
    // Note: We can't easily filter by course_id in details,
    // so we'll leave activity_log intact for history purposes

    return true;
  } catch (error) {
    console.error('resetCourseProgress error:', error);
    return false;
  }
}

// ============================================
// MODULE-SPECIFIC RESET FUNCTIONS
// ============================================

export type ResetModule = 'quiz' | 'fill-in' | 'transform' | 'vocab-match' | 'flashcards' | 'lessons';

export async function resetModuleProgress(
  userId: string,
  courseId: string,
  module: ResetModule
): Promise<boolean> {
  try {
    switch (module) {
      case 'quiz':
        await supabase
          .from('quiz_results')
          .delete()
          .eq('user_id', userId)
          .eq('course_id', courseId)
          .eq('mode', 'quiz');
        break;

      case 'fill-in':
        await supabase
          .from('quiz_results')
          .delete()
          .eq('user_id', userId)
          .eq('course_id', courseId)
          .eq('mode', 'fill-in');
        break;

      case 'transform':
        await supabase
          .from('quiz_results')
          .delete()
          .eq('user_id', userId)
          .eq('course_id', courseId)
          .eq('mode', 'transform');
        // Also delete ai_feedback for transform
        await supabase
          .from('ai_feedback')
          .delete()
          .eq('user_id', userId)
          .eq('course_id', courseId)
          .eq('type', 'transform_quiz');
        break;

      case 'vocab-match':
        // Delete only vocab_ prefixed flashcard progress
        const { data: vocabProgress } = await supabase
          .from('flashcard_progress')
          .select('id, flashcard_id')
          .eq('user_id', userId)
          .eq('course_id', courseId);

        if (vocabProgress) {
          const vocabIds = vocabProgress
            .filter(p => p.flashcard_id.startsWith('vocab_'))
            .map(p => p.id);
          if (vocabIds.length > 0) {
            await supabase
              .from('flashcard_progress')
              .delete()
              .in('id', vocabIds);
          }
        }
        break;

      case 'flashcards':
        // Delete non-vocab flashcard progress
        const { data: flashProgress } = await supabase
          .from('flashcard_progress')
          .select('id, flashcard_id')
          .eq('user_id', userId)
          .eq('course_id', courseId);

        if (flashProgress) {
          const flashIds = flashProgress
            .filter(p => !p.flashcard_id.startsWith('vocab_'))
            .map(p => p.id);
          if (flashIds.length > 0) {
            await supabase
              .from('flashcard_progress')
              .delete()
              .in('id', flashIds);
          }
        }
        break;

      case 'lessons':
        await supabase
          .from('lesson_progress')
          .delete()
          .eq('user_id', userId)
          .eq('course_id', courseId);
        break;
    }

    return true;
  } catch (error) {
    console.error('resetModuleProgress error:', error);
    return false;
  }
}

export async function resetAllProgress(userId: string): Promise<boolean> {
  try {
    // Delete all quiz results
    await supabase
      .from('quiz_results')
      .delete()
      .eq('user_id', userId);

    // Delete all flashcard progress
    await supabase
      .from('flashcard_progress')
      .delete()
      .eq('user_id', userId);

    // Delete all lesson progress
    await supabase
      .from('lesson_progress')
      .delete()
      .eq('user_id', userId);

    // Delete all AI feedback
    await supabase
      .from('ai_feedback')
      .delete()
      .eq('user_id', userId);

    // Delete daily streaks
    await supabase
      .from('daily_streaks')
      .delete()
      .eq('user_id', userId);

    return true;
  } catch (error) {
    console.error('resetAllProgress error:', error);
    return false;
  }
}

// ============================================
// PROFILE ACTIVITY FUNCTIONS
// ============================================

export async function getUserActivityLog(
  userId: string,
  limit = 200
): Promise<ActivityLog[]> {
  const { data, error } = await supabase
    .from('activity_log')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching user activity log:', error);
    return [];
  }

  return data as ActivityLog[];
}

export async function getUserDailyStreaks(
  userId: string,
  limit = 365
): Promise<DailyStreak[]> {
  const { data, error } = await supabase
    .from('daily_streaks')
    .select('*')
    .eq('user_id', userId)
    .order('date', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching user daily streaks:', error);
    return [];
  }

  return data as DailyStreak[];
}

export async function getAllUserQuizResults(
  userId: string,
  limit = 100
): Promise<QuizResult[]> {
  const { data, error } = await supabase
    .from('quiz_results')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching all user quiz results:', error);
    return [];
  }

  return data as QuizResult[];
}

export async function getAllUserFlashcardProgress(
  userId: string
): Promise<FlashcardProgress[]> {
  const { data, error } = await supabase
    .from('flashcard_progress')
    .select('*')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching all flashcard progress:', error);
    return [];
  }

  return data as FlashcardProgress[];
}

// ============================================
// SMART QUESTION HISTORY
// ============================================

export async function getWrongQuestionsHistory(
  userId: string,
  courseId: string,
  mode: string
): Promise<{ wrongHistory: string[]; lastQuizQuestionIds: string[] }> {
  try {
    const { data: results } = await supabase
      .from('quiz_results')
      .select('wrong_questions, details')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .eq('mode', mode)
      .order('created_at', { ascending: false })
      .limit(5);

    if (!results || results.length === 0) {
      return { wrongHistory: [], lastQuizQuestionIds: [] };
    }

    // Collect all wrong questions from last 5 quizzes
    const wrongHistory: string[] = [];
    for (const result of results) {
      if (result.wrong_questions && Array.isArray(result.wrong_questions)) {
        wrongHistory.push(...result.wrong_questions);
      }
    }

    // Get question IDs from last quiz
    const lastQuizQuestionIds: string[] =
      results[0]?.details?.all_question_ids || [];

    return { wrongHistory, lastQuizQuestionIds };
  } catch (error) {
    console.error('getWrongQuestionsHistory error:', error);
    return { wrongHistory: [], lastQuizQuestionIds: [] };
  }
}

// ============================================
// MESSAGES / NOTIFICATIONS
// ============================================

export async function getUnreadMessages(userId: string): Promise<Message[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('*, message_reads!left(user_id)')
    .eq('active', true)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching messages:', error);
    return [];
  }

  // Filter to only unread messages for this user
  return (data || []).filter((msg: Record<string, unknown>) => {
    const reads = msg.message_reads as Array<{ user_id: string }> | null;
    return !reads || !reads.some(r => r.user_id === userId);
  }) as Message[];
}

export async function getAllMessages(): Promise<Message[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('active', true)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching messages:', error);
    return [];
  }

  return data as Message[];
}

export async function getReadMessageIds(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('message_reads')
    .select('message_id')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching read messages:', error);
    return [];
  }

  return (data || []).map(r => r.message_id);
}

export async function markMessageRead(userId: string, messageId: string): Promise<void> {
  const { error } = await supabase
    .from('message_reads')
    .upsert({ user_id: userId, message_id: messageId });

  if (error) {
    console.error('Error marking message read:', error);
  }
}
